import React from 'react';
import type { ReactNode } from 'react';
import { Pressable } from 'react-native-gesture-handler';

import { Text } from '@/components/TextComponent';
import type { TextProps } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import type { BoxProps } from '@/components/lib/components';

import { ds } from '../lib/design.system';

/** Where the cash-out controls render: compact entry-card pills or the larger entry-detail button dock. */
export type CashOutControlsVariant = 'card' | 'detail';

/** Per-variant button metrics, shared by every cash-out phase so the region reads as one control. */
export const CASH_OUT_PILL: Record<
    CashOutControlsVariant,
    {
        height: number;
        borderRadius: BoxProps['borderRadius'];
        textVariant: TextProps['variant'];
        labelGap: BoxProps['gap'];
        loaderSize: number;
    }
> = {
    card: { height: 44, borderRadius: 'r12', textVariant: 'titleMedium', labelGap: 's4', loaderSize: 20 },
    detail: { height: 48, borderRadius: 'r14', textVariant: 'titleMedium2', labelGap: 's6', loaderSize: 24 },
};

export type EntryCardCashOutProps = {
    label: string;
    amount: string;
    leadingIcon?: ReactNode;
    onPress: () => void;
    variant?: CashOutControlsVariant;
};

export const EntryCardCashOutButton = ({
    label,
    amount,
    leadingIcon,
    onPress,
    variant = 'card',
}: EntryCardCashOutProps) => {
    const { height, borderRadius, textVariant, labelGap } = CASH_OUT_PILL[variant];
    return (
        <Pressable onPress={onPress} accessibilityRole="button">
            <Box
                height={height}
                borderRadius={borderRadius}
                backgroundColor={ds.colorToken.background.brand.emphasis}
                paddingHorizontal="s16"
                alignItems="center"
                justifyContent="center"
            >
                <Row alignItems="center" gap={labelGap}>
                    <Text variant={textVariant} color={ds.colorToken.text.primary}>
                        {label}
                    </Text>
                    <Text variant={textVariant} color={ds.colorToken.text.primary}>
                        •
                    </Text>
                    <Row alignItems="center" gap="s4">
                        {leadingIcon}
                        <Text variant={textVariant} color={ds.colorToken.text.primary}>
                            {amount}
                        </Text>
                    </Row>
                </Row>
            </Box>
        </Pressable>
    );
};
